import React, { useMemo, useState } from 'react';
import { Thumbnail } from '@remotion/player';
import { Video, computeTimeline, FPS, type VideoSpec } from '@hyvay/video-engine';
import type { VideoListItem } from '../../services/video.service';
import { RenderMenu } from './RenderActions';
import type { MediaAsset } from '../../services/studio.service';
import { FONT_MONO, FONT_SANS } from '../landing/landingData';
import { fmtBytes } from '../landing/format';
import { ChevronRight, emptyIcon, emptyText, emptyTitle, emptyWrap, iconBtn, listHead, primaryBtn, segBtn, toggleBtn } from '../landing/WorkspaceSection';
import { FORMAT_OPTIONS, STATUS_LABEL } from './videoUi';

type Tab = 'projects' | 'renders' | 'media';

interface Props {
    projects: VideoListItem[] | null;
    media: MediaAsset[] | null;
    onOpenProject: (id: string) => void;
    /** Focuses the composer above; the "new video" form lives there now. */
    onCreate: () => void;
    /** Hasil video: play the MP4 in the page (opening its URL downloads it: attachment header). */
    onViewRender: (v: VideoListItem) => void;
    onPostRender: (v: VideoListItem) => void;
}

const STEP: Record<Tab, number> = { projects: 6, renders: 6, media: 8 };

/**
 * Poster for a finished MP4, drawn from the spec (in the render's own aspect). Not a
 * <video> of the file: each one downloads most of a ~50 MB MP4, and the player opened
 * on click then waits behind those downloads of the same URL.
 */
const renderSpec = (v: VideoListItem): VideoSpec => (v.lastDone?.format ? { ...v.spec, format: v.lastDone.format } : v.spec);

const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const formatLabel = (f: string | null | undefined) => FORMAT_OPTIONS.find((o) => o.value === f)?.label ?? '';

/** Project pill: last MP4 status, or DRAF before the first render. */
const statusPill = (v: VideoListItem) => {
    const s = v.lastRender?.status;
    const done = s === 'DONE';
    return {
        text: s ? STATUS_LABEL[s].toUpperCase() : 'DRAF',
        bg: done ? 'var(--hv-mint)' : 'var(--hv-sand)',
        fg: done ? 'var(--hv-mint-ink)' : '#8A6D2B',
    };
};

/**
 * /video workspace: the landing page's Projects/Media card, with an extra
 * "Hasil video" tab for each project's latest finished MP4.
 */
export const VideoWorkspace: React.FC<Props> = ({ projects, media, onOpenProject, onCreate, onViewRender, onPostRender }) => {
    const [tab, setTab] = useState<Tab>('projects');
    const [view, setView] = useState<'grid' | 'list'>('grid');
    const [query, setQuery] = useState('');
    const [shown, setShown] = useState<Record<Tab, number>>(STEP);

    // Newest finished MP4 per project, even when a later render is still running or failed.
    const renders = useMemo(() => (projects || []).filter((v) => v.lastDone?.url), [projects]);
    const q = query.trim().toLowerCase();
    const projList = (projects || []).filter((v) => !q || v.name.toLowerCase().includes(q));
    const renderList = renders.filter((v) => !q || v.name.toLowerCase().includes(q));
    const mediaList = (media || []).filter((m) => !q || m.originalName.toLowerCase().includes(q));

    const loading = tab === 'media' ? media === null : projects === null;
    const total = tab === 'projects' ? projects?.length ?? 0 : tab === 'renders' ? renders.length : media?.length ?? 0;
    const count = tab === 'projects' ? projList.length : tab === 'renders' ? renderList.length : mediaList.length;
    const n = Math.min(shown[tab], count);
    const empty = !loading && total === 0;
    const noResults = !loading && total > 0 && count === 0;
    const filled = !loading && count > 0;

    const placeholder = tab === 'projects' ? 'Cari project' : tab === 'renders' ? 'Cari hasil video' : 'Cari media';

    return (
        <div style={{ marginTop: 40 }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.18em', color: 'var(--brand,#13422C)', marginBottom: 14 }}>VIDEO KAMU</div>
            <div style={{ borderRadius: 20, overflow: 'hidden', background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 34px 60px -40px var(--hv-shadow)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 46, padding: '0 16px', background: 'var(--hv-card-2)', borderBottom: '1px solid var(--hv-line)' }}>
                    <span style={{ width: 10, height: 10, borderRadius: 999, background: 'var(--hv-line-2)' }} />
                    <span style={{ width: 10, height: 10, borderRadius: 999, background: 'var(--hv-line-2)' }} />
                    <span style={{ width: 10, height: 10, borderRadius: 999, background: 'var(--hv-line-2)' }} />
                </div>
                <div style={{ padding: '22px 24px 30px' }}>
                    <div className="hv-ws-bar" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
                        <div className="hv-seg-wrap" style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', width: 'max-content' }}>
                            <button type="button" onClick={() => setTab('projects')} style={segBtn(tab === 'projects')}>Project</button>
                            <button type="button" onClick={() => setTab('renders')} style={segBtn(tab === 'renders')}>Hasil video</button>
                            <button type="button" onClick={() => setTab('media')} style={segBtn(tab === 'media')}>Media</button>
                        </div>
                        <div style={{ flex: 1 }} />
                        <div className="hv-seg-wrap" style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', flex: 'none' }}>
                            <button type="button" onClick={() => setView('grid')} aria-label="Tampilan grid" style={iconBtn(view === 'grid')}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="7" height="7" rx="1.6" /><rect x="13" y="4" width="7" height="7" rx="1.6" /><rect x="4" y="13" width="7" height="7" rx="1.6" /><rect x="13" y="13" width="7" height="7" rx="1.6" /></svg>
                            </button>
                            <button type="button" onClick={() => setView('list')} aria-label="Tampilan daftar" style={iconBtn(view === 'list')}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6.5h16M4 12h16M4 17.5h16" /></svg>
                            </button>
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px', borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', minWidth: 190 }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', color: 'var(--hv-muted)' }}><circle cx="11" cy="11" r="6.6" /><path d="M16 16l4 4" /></svg>
                            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', fontFamily: FONT_SANS, fontSize: 13, color: 'var(--hv-ink)' }} />
                        </label>
                        <button type="button" onClick={onCreate} className="hv-h-brand-dark" style={primaryBtn}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                            Video baru
                        </button>
                    </div>

                    {loading && (
                        <div style={{ ...emptyWrap, padding: '48px 28px' }}>
                            <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.16em', color: 'var(--hv-muted)' }}>MEMUAT…</div>
                        </div>
                    )}

                    {empty && <EmptyState tab={tab} />}

                    {filled && tab === 'projects' && (view === 'grid' ? (
                        <div className="hv-cols-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
                            {projList.slice(0, n).map((v) => {
                                const c = statusPill(v);
                                return (
                                    <div key={v.id} onClick={() => onOpenProject(v.id)} className="hv-h-lift" style={cardStyle}>
                                        <div style={{ position: 'relative', height: 220, background: 'var(--hv-page-2)' }}>
                                            <VideoThumb spec={v.spec} />
                                            <Pill {...c} />
                                        </div>
                                        <CardFoot title={v.name} sub={`${formatLabel(v.format)} · ${v.scenes} scene · ${fmtDate(v.updatedAt)}`} />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <ListBox cols="minmax(0,2.4fr) 110px minmax(0,1fr) 40px" head={['PROJECT', 'STATUS', 'DIUBAH', '']} hideCol={2}>
                            {projList.slice(0, n).map((v) => {
                                const c = statusPill(v);
                                return (
                                    <div key={v.id} onClick={() => onOpenProject(v.id)} className="hv-lrow hv-h-page2" style={rowStyle('minmax(0,2.4fr) 110px minmax(0,1fr) 40px')}>
                                        <RowName title={v.name} sub={`${formatLabel(v.format)} · ${v.scenes} scene`} thumb={<VideoThumb spec={v.spec} />} />
                                        <Pill {...c} inline />
                                        <div className="hv-lcol" style={cellText}>{fmtDate(v.updatedAt)}</div>
                                        <span style={chevStyle}><ChevronRight size={12} /></span>
                                    </div>
                                );
                            })}
                        </ListBox>
                    ))}

                    {filled && tab === 'renders' && (view === 'grid' ? (
                        <div className="hv-cols-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
                            {renderList.slice(0, n).map((v) => {
                                const r = v.lastDone!;
                                return (
                                    <div key={v.id} role="button" tabIndex={0} onClick={() => onViewRender(v)} onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && onViewRender(v)} className="hv-h-lift" style={{ ...cardStyle, color: 'var(--hv-ink)' }}>
                                        <div style={{ position: 'relative', height: 220, background: 'var(--hv-page-2)' }}>
                                            <VideoThumb spec={renderSpec(v)} />
                                            <PlayBadge />
                                        </div>
                                        <CardFoot title={v.name} sub={[formatLabel(r.format ?? v.format), r.bytes ? fmtBytes(r.bytes) : '', fmtDate(r.finishedAt)].filter(Boolean).join(' · ')} action={<RenderMenu render={r} onView={() => onViewRender(v)} onPost={() => onPostRender(v)} />} />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <ListBox cols="minmax(0,2.4fr) 80px 90px minmax(0,1fr) 40px" head={['VIDEO', 'FORMAT', 'UKURAN', 'SELESAI', '']} hideCol={3}>
                            {renderList.slice(0, n).map((v) => {
                                const r = v.lastDone!;
                                return (
                                    <div key={v.id} role="button" tabIndex={0} onClick={() => onViewRender(v)} onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && onViewRender(v)} className="hv-lrow hv-h-page2" style={{ ...rowStyle('minmax(0,2.4fr) 80px 90px minmax(0,1fr) 40px'), color: 'var(--hv-ink)' }}>
                                        <RowName title={v.name} sub="MP4" thumb={<VideoThumb spec={renderSpec(v)} />} />
                                        <div style={cellText}>{formatLabel(r.format ?? v.format)}</div>
                                        <div style={cellText}>{r.bytes ? fmtBytes(r.bytes) : '–'}</div>
                                        <div className="hv-lcol" style={cellText}>{fmtDate(r.finishedAt)}</div>
                                        <div style={{ justifySelf: 'end' }}><RenderMenu render={r} onView={() => onViewRender(v)} onPost={() => onPostRender(v)} /></div>
                                    </div>
                                );
                            })}
                        </ListBox>
                    ))}

                    {filled && tab === 'media' && (view === 'grid' ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 14 }}>
                            {mediaList.slice(0, n).map((m) => (
                                <a key={m.assetId} href={m.url} target="_blank" rel="noreferrer" title={m.originalName} className="hv-h-brand-bd" style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', aspectRatio: '1', cursor: 'pointer', transition: 'border-color 0.25s ease', display: 'block' }}>
                                    <MediaPreview m={m} />
                                    <span style={{ position: 'absolute', left: 8, bottom: 8, fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.1em', padding: '2px 7px', borderRadius: 99, background: 'rgba(0,0,0,.6)', color: '#fff' }}>{m.kind === 'video' ? 'VIDEO' : 'GAMBAR'}</span>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <ListBox cols="minmax(0,2.4fr) 96px 80px minmax(0,1fr)" head={['FILE', 'TIPE', 'UKURAN', 'DIUNGGAH']} hideCol={3}>
                            {mediaList.slice(0, n).map((m) => (
                                <a key={m.assetId} href={m.url} target="_blank" rel="noreferrer" className="hv-lrow hv-h-page2" style={{ ...rowStyle('minmax(0,2.4fr) 96px 80px minmax(0,1fr)'), padding: '10px 16px', color: 'var(--hv-ink)' }}>
                                    <RowName title={m.originalName} thumb={<MediaPreview m={m} />} square />
                                    <div style={cellText}>{m.kind === 'video' ? 'Video' : 'Gambar'}</div>
                                    <div style={cellText}>{fmtBytes(m.bytes)}</div>
                                    <div className="hv-lcol" style={cellText}>{fmtDate(m.createdAt)}</div>
                                </a>
                            ))}
                        </ListBox>
                    ))}

                    {filled && count > STEP[tab] && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 26 }}>
                            <button type="button" onClick={() => setShown((s) => ({ ...s, [tab]: n < count ? count : STEP[tab] }))} className="hv-h-page2-bd" style={toggleBtn}>
                                {n < count ? 'Tampilkan lagi' : 'Tampilkan sedikit'}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" style={{ transform: `rotate(${n < count ? 0 : 180}deg)` }}><path d="M6 9l6 6 6-6" /></svg>
                            </button>
                        </div>
                    )}

                    {noResults && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '52px 28px', borderRadius: 16, border: '1.5px dashed var(--hv-line-2)', background: 'var(--hv-page-2)' }}>
                            <div style={{ ...emptyTitle, fontSize: 24 }}>Tidak ada yang cocok dengan “{query}”</div>
                            <p style={{ ...emptyText, maxWidth: 320 }}>Coba kata yang lebih pendek, atau hapus pencarian untuk melihat semuanya.</p>
                            <button type="button" onClick={() => setQuery('')} className="hv-h-card-bd" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, marginTop: 12, padding: '0 18px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Hapus pencarian</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const cardStyle: React.CSSProperties = { borderRadius: 14, overflow: 'hidden', background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', cursor: 'pointer', transition: 'border-color 0.25s ease,transform 0.3s cubic-bezier(0.2,0.9,0.24,1)' };
const rowStyle = (cols: string): React.CSSProperties => ({ display: 'grid', gridTemplateColumns: cols, alignItems: 'center', gap: 16, padding: '11px 16px', borderBottom: '1px solid var(--hv-line)', cursor: 'pointer', transition: 'background 0.18s ease' });
const cellText: React.CSSProperties = { fontSize: 12.5, color: 'var(--hv-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const chevStyle: React.CSSProperties = { width: 26, height: 26, borderRadius: 999, border: '1px solid var(--hv-line-2)', color: 'var(--hv-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', justifySelf: 'end' };

const Pill: React.FC<{ text: string; bg: string; fg: string; inline?: boolean }> = ({ text, bg, fg, inline }) => (
    <span style={{ ...(inline ? { width: 'max-content' } : { position: 'absolute', top: 10, left: 10, pointerEvents: 'none' }), display: 'inline-flex', alignItems: 'center', gap: 5, height: inline ? 22 : 23, padding: '0 10px', borderRadius: 999, background: bg, color: fg, fontFamily: FONT_MONO, fontSize: inline ? 9 : 9.5, letterSpacing: '0.1em' }}>
        <span style={{ width: 5, height: 5, borderRadius: 999, background: fg }} />{text}
    </span>
);

/** `action` replaces the decorative chevron (e.g. the Hasil video menu). */
const CardFoot: React.FC<{ title: string; sub: string; action?: React.ReactNode }> = ({ title, sub, action }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
            <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--hv-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
        </div>
        {action ?? (
            <span style={{ width: 28, height: 28, borderRadius: 999, border: '1px solid var(--hv-line-2)', color: 'var(--hv-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <ChevronRight size={13} />
            </span>
        )}
    </div>
);

/** `hideCol` is the header column that collapses on narrow screens (hv-lcol). */
const ListBox: React.FC<{ cols: string; head: string[]; hideCol: number; children: React.ReactNode }> = ({ cols, head, hideCol, children }) => (
    <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hv-line)', background: 'var(--hv-card)' }}>
        <div className="hv-lrow" style={listHead(cols)}>
            {head.map((h, i) => <span key={i} className={i === hideCol ? 'hv-lcol' : undefined}>{h}</span>)}
        </div>
        {children}
    </div>
);

const RowName: React.FC<{ title: string; sub?: string; thumb: React.ReactNode; square?: boolean }> = ({ title, sub, thumb, square }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <div style={{ width: square ? 38 : 52, height: 38, borderRadius: 7, overflow: 'hidden', background: 'var(--hv-card-2)', border: '1px solid var(--hv-line)', flex: 'none', position: 'relative' }}>{thumb}</div>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
            {sub && <div style={{ marginTop: 2, fontSize: 12, color: 'var(--hv-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
        </div>
    </div>
);

const MediaPreview: React.FC<{ m: MediaAsset }> = ({ m }) =>
    m.kind === 'video'
        ? <video src={m.url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : <img src={m.url} alt={m.alt || m.originalName} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;

const PlayBadge: React.FC = () => (
    <span style={{ position: 'absolute', inset: 0, margin: 'auto', width: 46, height: 46, borderRadius: 999, background: 'rgba(0,0,0,.55)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l11-6.5z" /></svg>
    </span>
);

const EmptyState: React.FC<{ tab: Tab }> = ({ tab }) => {
    const copy = {
        projects: { title: 'Belum ada video', text: 'Ceritakan idemu di kotak di atas, project pertamamu akan muncul di sini.' },
        renders: { title: 'Belum ada hasil video', text: 'Buka sebuah project lalu render MP4. File yang sudah selesai muncul di sini.' },
        media: { title: 'Belum ada media', text: 'Gambar & video yang kamu lampirkan ke project video akan muncul di sini.' },
    }[tab];
    return (
        <div style={emptyWrap}>
            <span style={emptyIcon}>
                <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5.5" width="17" height="13" rx="2" /><path d="M10 9.5v5l4.5-2.5z" /></svg>
            </span>
            <div style={emptyTitle}>{copy.title}</div>
            <p style={emptyText}>{copy.text}</p>
        </div>
    );
};

/**
 * Live poster frame of a video, drawn in the browser from its spec. Picks a moment
 * inside the first non-clip scene (text and photos settle by then) so the card does
 * not have to load a large raw recording; falls back to the first scene.
 */
const VideoThumb: React.FC<{ spec?: VideoSpec }> = ({ spec }) => {
    const t = useMemo(() => (spec ? computeTimeline(spec, {}) : null), [spec]);
    // position: relative so overflow clips the player's absolute layers (a 9:16 frame
    // spilled over the card footer and swallowed clicks on its "›" menu).
    const box: React.CSSProperties = { position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: 'var(--hv-page-2)', display: 'grid', placeItems: 'center' };
    if (!spec || !t || !spec.scenes.length) return <div style={box} />;
    const idx = Math.max(0, spec.scenes.findIndex((s) => s.type !== 'video'));
    const scene = t.scenes[idx];
    const frame = Math.min(scene.from + Math.min(60, scene.frames - 14), t.durationInFrames - 1);
    return (
        <div style={box}>
            <div style={{ height: '100%', aspectRatio: `${t.width} / ${t.height}`, maxWidth: '100%' }}>
                <Thumbnail
                    component={Video}
                    inputProps={{ spec, assets: {} }}
                    compositionWidth={t.width}
                    compositionHeight={t.height}
                    durationInFrames={t.durationInFrames}
                    fps={FPS}
                    frameToDisplay={Math.max(0, frame)}
                    style={{ width: '100%', height: '100%' }}
                    errorFallback={() => <div style={{ width: '100%', height: '100%', background: 'var(--hv-page-2)' }} />}
                />
            </div>
        </div>
    );
};
